import { useTranslation } from 'react-i18next';
import { Box, Typography, Select, GLPGrid, portalChrome } from '@glplus/glpweb-dsl';
import { useVendor } from '../lib/CapabilitiesContext';
import { currentLanguage, switchPortalLanguage } from '../lib/portalLanguage';

/**
 * Vendor portal footer — thin utility bar (copyright · version · support ·
 * language) built from DSL primitives + GLPGrid, painted with the vendor
 * portalTheme footer color (solid deep orange). No MUI.
 */

const CHROME = portalChrome.vendor;
const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string) ?? '1.0.0';

export default function MainFooter() {
  const { t, i18n } = useTranslation();
  const year = new Date().getFullYear();

  const { data } = useVendor();
  const switchLanguage = (lang: string) => switchPortalLanguage(i18n, lang, !!data);

  return (
    <Box
      component="footer"
      aria-label={t('footer.ariaLabel')}
      sx={{
        bgcolor: CHROME.footerBg ?? '#DF6514',
        color: CHROME.footerText,
        px: { xs: '16px', sm: '32px', lg: '65px' },
        py: 2,
      }}
    >
      <GLPGrid container columns={12} alignItems="center" spacing={1}>
        <GLPGrid size={{ xs: 12, sm: 6 }}>
          <Typography sx={{ color: CHROME.footerText, fontWeight: 600, fontSize: '14px' }}>
            {t('footer.copyright', { year })}
          </Typography>
        </GLPGrid>
        <GLPGrid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 120 }}>
              <Select
                ariaLabel={t('footer.language')}
                value={currentLanguage(i18n)}
                onChange={(_e, value) => switchLanguage(value)}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'es', label: 'Español' },
                ]}
                size="small"
              />
            </Box>
            <Typography
              component="a"
              href="mailto:support@glplusinc.com"
              sx={{
                color: CHROME.footerText,
                fontWeight: 600,
                fontSize: '14px',
                textDecoration: 'underline',
                '&:hover': { opacity: 0.85 },
              }}
            >
              {t('footer.support')}
            </Typography>
            <Typography sx={{ color: CHROME.footerText, opacity: 0.85, fontSize: '14px' }}>
              v{APP_VERSION}
            </Typography>
          </Box>
        </GLPGrid>
      </GLPGrid>
    </Box>
  );
}
